import * as THREE from 'three';
import { TILE } from './grid';

/**
 * Statische botsingswereld.
 *
 * Geen physics-engine: het level registreert asgerichte blokken (AABB's) in een uniform
 * grid, en bewegende entiteiten zijn cirkels in het XZ-vlak. Botsingen worden per as
 * opgelost, zodat je langs een muur glijdt in plaats van eraan vast te plakken.
 *
 * Y speelt geen rol in de beweging — de wereld is isometrisch en de speler loopt over één
 * begaanbaar niveau. Hoogte doet wel mee bij het schieten, via `blocksLineOfSight`.
 */

export interface Obstacle {
  /** Middelpunt in het XZ-vlak. */
  readonly x: number;
  readonly z: number;
  /** Halve afmetingen. */
  readonly halfX: number;
  readonly halfZ: number;
  /** Hoogte boven de grond — bepaalt of het zicht en kogels geblokkeerd worden. */
  readonly height: number;
}

export type CollisionHandle = Obstacle;

/** Onder deze hoogte kun je overheen kijken en schieten: hekjes, kratten, stoepranden. */
const COVER_HEIGHT = 1.1;

/**
 * Extra ruimte waarmee `resolveOverlap` iemand buiten een blok zet. Zonder deze marge kom je
 * exact op de rand uit, waar afrondingsfouten je meteen weer als overlappend laten tellen.
 */
const SEPARATION_SKIN = 1e-3;

export class CollisionWorld {
  readonly #cells = new Map<string, Obstacle[]>();
  readonly #all: Obstacle[] = [];
  readonly #handleCells = new Map<CollisionHandle, string[]>();

  /** Voeg een blokkerend blok toe. `height` bepaalt of het ook kogels tegenhoudt. */
  add(obstacle: Obstacle): CollisionHandle {
    this.#all.push(obstacle);
    const minX = Math.floor((obstacle.x - obstacle.halfX) / TILE);
    const maxX = Math.floor((obstacle.x + obstacle.halfX) / TILE);
    const minZ = Math.floor((obstacle.z - obstacle.halfZ) / TILE);
    const maxZ = Math.floor((obstacle.z + obstacle.halfZ) / TILE);
    const keys: string[] = [];

    for (let cellX = minX; cellX <= maxX; cellX++) {
      for (let cellZ = minZ; cellZ <= maxZ; cellZ++) {
        const key = cellKey(cellX, cellZ);
        keys.push(key);
        let bucket = this.#cells.get(key);
        if (!bucket) {
          bucket = [];
          this.#cells.set(key, bucket);
        }
        bucket.push(obstacle);
      }
    }
    this.#handleCells.set(obstacle, keys);
    return obstacle;
  }

  remove(handle: CollisionHandle): void {
    const index = this.#all.indexOf(handle);
    if (index < 0) return;
    this.#all.splice(index, 1);

    for (const key of this.#handleCells.get(handle) ?? []) {
      const bucket = this.#cells.get(key);
      if (!bucket) continue;
      const bucketIndex = bucket.indexOf(handle);
      if (bucketIndex < 0) continue;
      bucket.splice(bucketIndex, 1);
      if (bucket.length === 0) this.#cells.delete(key);
    }
    this.#handleCells.delete(handle);
  }

  /** Registreer een mesh op zijn wereld-bounding box. Handig voor level-geometrie. */
  addFromObject(object: THREE.Object3D): CollisionHandle {
    object.updateMatrixWorld(true);
    const bounds = new THREE.Box3().setFromObject(object);
    const size = bounds.getSize(new THREE.Vector3());
    const center = bounds.getCenter(new THREE.Vector3());
    return this.add({
      x: center.x,
      z: center.z,
      halfX: size.x / 2,
      halfZ: size.z / 2,
      height: size.y,
    });
  }

  get obstacleCount(): number {
    return this.#all.length;
  }

  get obstacles(): readonly Obstacle[] {
    return this.#all;
  }

  obstaclesNear(x: number, z: number, radius: number): readonly Obstacle[] {
    return [...this.#candidates(x, z, radius)];
  }

  clear(): void {
    this.#cells.clear();
    this.#handleCells.clear();
    this.#all.length = 0;
  }

  /**
   * Verplaats een cirkel van `position` met `delta` en los botsingen op.
   *
   * De assen worden apart geprobeerd: eerst X, dan Z. Als X klemt maar Z vrij is, glijdt de
   * speler langs de muur verder — precies wat je wilt bij het navigeren van smalle
   * havenstraatjes.
   */
  moveCircle(position: THREE.Vector2, delta: THREE.Vector2, radius: number): THREE.Vector2 {
    let { x, y: z } = position;

    x = this.#sweepAxis(x, z, delta.x, radius, 'x');
    z = this.#sweepAxis(x, z, delta.y, radius, 'z');

    return position.set(x, z);
  }

  /**
   * Duw een cirkel uit elk blok waar hij middenin zit. Geeft terug of er iets verschoven is.
   *
   * `moveCircle` voorkomt dat je ergens in loopt, maar niet dat je ergens in wordt gezet: de
   * duw tussen personages onderling en de duw van een auto schrijven rechtstreeks in de
   * positie van een actor, zonder de statische wereld te raadplegen. Beland je zo in een
   * gevel, dan houdt de anti-teleport-guard in `#sweepAxis` je daar vast — die weigert immers
   * elke stap zolang je overlapt. Dit is de uitweg: kies per blok de kortste verplaatsing naar
   * buiten, en herhaal een paar keer voor het geval je in een binnenhoek van twee blokken zit.
   */
  resolveOverlap(position: THREE.Vector2, radius: number, iterations = 4): boolean {
    let moved = false;

    for (let pass = 0; pass < iterations; pass++) {
      let deepest: Obstacle | null = null;
      let deepestDepth = 0;

      for (const obstacle of this.#candidates(position.x, position.y, radius)) {
        const depth = penetrationDepth(obstacle, position.x, position.y, radius);
        if (depth <= deepestDepth) continue;
        deepest = obstacle;
        deepestDepth = depth;
      }

      if (!deepest) return moved;

      pushCircleOut(deepest, position, radius);
      moved = true;
    }

    return moved;
  }

  /** Staat er iets tussen twee punten dat kogels of zicht tegenhoudt? */
  blocksLineOfSight(from: THREE.Vector2, to: THREE.Vector2): boolean {
    return this.raycast(from, to) !== null;
  }

  /**
   * Eerste snijpunt van een straal met een blokkerend obstakel, of `null`.
   * Bemonstert de lijn in stappen van een halve tile — ruim genoeg voor hitscan-kogels op
   * stadsschaal, en veel goedkoper dan een exacte slab-test per obstakel.
   */
  raycast(from: THREE.Vector2, to: THREE.Vector2): THREE.Vector2 | null {
    const dx = to.x - from.x;
    const dz = to.y - from.y;
    const distance = Math.hypot(dx, dz);
    if (distance === 0) return null;

    const dirX = dx / distance;
    const dirZ = dz / distance;

    let nearest = Infinity;

    // Kandidaten verzamelen langs de straal, en ze daarna exact toetsen. De monsterpunten
    // liggen een tile uit elkaar en elk punt haalt de cellen binnen een tile eromheen op, dus
    // elke cel die de straal kruist komt langs. Het monsteren bepaalt hier dus alleen wélke
    // blokken meedoen — niet óf ze geraakt worden.
    const samples = Math.max(1, Math.ceil(distance / TILE));
    const seen = new Set<Obstacle>();

    for (let i = 0; i <= samples; i++) {
      const t = (i / samples) * distance;
      for (const obstacle of this.#candidates(from.x + dirX * t, from.y + dirZ * t, TILE)) {
        if (seen.has(obstacle)) continue;
        seen.add(obstacle);
        if (obstacle.height < COVER_HEIGHT) continue;
        const entry = rayEntryDistance(obstacle, from.x, from.y, dirX, dirZ, distance);
        if (entry !== null && entry < nearest) nearest = entry;
      }
    }

    if (nearest === Infinity) return null;
    return new THREE.Vector2(from.x + dirX * nearest, from.y + dirZ * nearest);
  }

  #sweepAxis(x: number, z: number, amount: number, radius: number, axis: 'x' | 'z'): number {
    if (amount === 0) return axis === 'x' ? x : z;

    const current = axis === 'x' ? x : z;
    const target = current + amount;
    if (!this.#overlapsAt(axis === 'x' ? target : x, axis === 'z' ? target : z, radius)) {
      return target;
    }

    // If the actor is already overlapping, never snap to a rectangle edge. That was the
    // warehouse teleport bug: a tiny corner overlap could resolve to the far side of a large
    // building. Holding position is less clever, but it is stable and lets the next safe input
    // move normally.
    if (this.#overlapsAt(x, z, radius)) return current;

    let safe = current;
    let blocked = target;
    for (let i = 0; i < 8; i++) {
      const mid = (safe + blocked) / 2;
      const midX = axis === 'x' ? mid : x;
      const midZ = axis === 'z' ? mid : z;
      if (this.#overlapsAt(midX, midZ, radius)) blocked = mid;
      else safe = mid;
    }

    return safe;
  }

  /** Obstakels in de cellen rond een punt. Marge zorgt dat randgevallen meekomen. */
  *#candidates(x: number, z: number, margin = 0): Generator<Obstacle> {
    const minX = Math.floor((x - margin) / TILE);
    const maxX = Math.floor((x + margin) / TILE);
    const minZ = Math.floor((z - margin) / TILE);
    const maxZ = Math.floor((z + margin) / TILE);

    const seen = new Set<Obstacle>();
    for (let cellX = minX; cellX <= maxX; cellX++) {
      for (let cellZ = minZ; cellZ <= maxZ; cellZ++) {
        const bucket = this.#cells.get(cellKey(cellX, cellZ));
        if (!bucket) continue;
        for (const obstacle of bucket) {
          if (seen.has(obstacle)) continue;
          seen.add(obstacle);
          yield obstacle;
        }
      }
    }
  }

  #overlapsAt(x: number, z: number, radius: number): boolean {
    for (const obstacle of this.#candidates(x, z, radius)) {
      if (overlapsCircle(obstacle, x, z, radius)) return true;
    }
    return false;
  }
}

function cellKey(cellX: number, cellZ: number): string {
  return `${cellX},${cellZ}`;
}

/**
 * Afstand tot waar een straal dit blok binnengaat, of `null` als hij het mist.
 *
 * De klassieke slab-test: per as het interval bepalen waarin de straal binnen het blok valt,
 * en die intervallen snijden. Exact, en goedkoper dan wat hier eerst stond — dat bemonsterde
 * de lijn om de twee meter en toetste alleen op die punten, waardoor een bolder van 56 cm of
 * een lantaarnpaal ertussen simpelweg niet bestond voor een kogel. `Combat` schaafde het
 * gevonden punt daarna nog met zeven extra raycasts bij; dat is nu niet meer nodig.
 *
 * Blokken waar het beginpunt al ín ligt tellen niet mee: schiet je met je rug tegen een muur,
 * dan hoor je niet je eigen dekking te raken.
 */
function rayEntryDistance(
  obstacle: Obstacle,
  originX: number,
  originZ: number,
  dirX: number,
  dirZ: number,
  maxDistance: number,
): number | null {
  if (contains(obstacle, originX, originZ)) return null;

  let entry = 0;
  let exit = maxDistance;

  const bounds: Array<[number, number, number, number]> = [
    [originX, dirX, obstacle.x - obstacle.halfX, obstacle.x + obstacle.halfX],
    [originZ, dirZ, obstacle.z - obstacle.halfZ, obstacle.z + obstacle.halfZ],
  ];

  for (const [origin, direction, min, max] of bounds) {
    if (Math.abs(direction) < 1e-9) {
      // Evenwijdig aan deze as: of je zit binnen de strook, of je raakt hem nooit.
      if (origin < min || origin > max) return null;
      continue;
    }
    const first = (min - origin) / direction;
    const second = (max - origin) / direction;
    entry = Math.max(entry, Math.min(first, second));
    exit = Math.min(exit, Math.max(first, second));
    if (entry > exit) return null;
  }

  return entry;
}

function contains(obstacle: Obstacle, x: number, z: number): boolean {
  return (
    Math.abs(x - obstacle.x) <= obstacle.halfX && Math.abs(z - obstacle.z) <= obstacle.halfZ
  );
}

/** Hoe diep steekt een cirkel in dit blok? 0 als hij er buiten ligt. */
function penetrationDepth(obstacle: Obstacle, x: number, z: number, radius: number): number {
  const closestX = THREE.MathUtils.clamp(x, obstacle.x - obstacle.halfX, obstacle.x + obstacle.halfX);
  const closestZ = THREE.MathUtils.clamp(z, obstacle.z - obstacle.halfZ, obstacle.z + obstacle.halfZ);
  const dx = x - closestX;
  const dz = z - closestZ;
  const distanceSq = dx * dx + dz * dz;

  if (distanceSq >= radius * radius) return 0;
  if (distanceSq > 1e-8) return radius - Math.sqrt(distanceSq);

  // Middelpunt zit in het blok: de diepte is de afstand tot de dichtstbijzijnde zijde.
  return radius + Math.min(
    obstacle.halfX - Math.abs(x - obstacle.x),
    obstacle.halfZ - Math.abs(z - obstacle.z),
  );
}

/** Verplaats een cirkel langs de kortste weg tot hij dit blok net niet meer raakt. */
function pushCircleOut(obstacle: Obstacle, position: THREE.Vector2, radius: number): void {
  const closestX = THREE.MathUtils.clamp(position.x, obstacle.x - obstacle.halfX, obstacle.x + obstacle.halfX);
  const closestZ = THREE.MathUtils.clamp(position.y, obstacle.z - obstacle.halfZ, obstacle.z + obstacle.halfZ);
  const dx = position.x - closestX;
  const dz = position.y - closestZ;
  const distanceSq = dx * dx + dz * dz;

  if (distanceSq > 1e-8) {
    const distance = Math.sqrt(distanceSq);
    const push = (radius - distance) + SEPARATION_SKIN;
    position.x += (dx / distance) * push;
    position.y += (dz / distance) * push;
    return;
  }

  const toRight = obstacle.x + obstacle.halfX - position.x;
  const toLeft = position.x - (obstacle.x - obstacle.halfX);
  const toFront = obstacle.z + obstacle.halfZ - position.y;
  const toBack = position.y - (obstacle.z - obstacle.halfZ);
  const smallest = Math.min(toRight, toLeft, toFront, toBack);

  if (smallest === toRight) position.x += toRight + radius + SEPARATION_SKIN;
  else if (smallest === toLeft) position.x -= toLeft + radius + SEPARATION_SKIN;
  else if (smallest === toFront) position.y += toFront + radius + SEPARATION_SKIN;
  else position.y -= toBack + radius + SEPARATION_SKIN;
}

function overlapsCircle(obstacle: Obstacle, x: number, z: number, radius: number): boolean {
  const closestX = THREE.MathUtils.clamp(x, obstacle.x - obstacle.halfX, obstacle.x + obstacle.halfX);
  const closestZ = THREE.MathUtils.clamp(z, obstacle.z - obstacle.halfZ, obstacle.z + obstacle.halfZ);
  return (x - closestX) ** 2 + (z - closestZ) ** 2 < radius * radius;
}
